import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { Field, SingleIconSelect } from '@makyoapp/makyo-ui'

import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import {
  BuilderOverlay,
  getDirectionLabel,
  getDrawerSize,
  overlayPlacements,
  switchDirectionPlacement,
} from '@/shared/overlay'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'

interface OverlaySettingBehaviorProps {
  overlay: BuilderOverlay
  index: number
  search: string
}

export const OverlaySettingBehavior = ({ overlay, index, search }: OverlaySettingBehaviorProps) => {
  const { t } = useTranslation()
  const { updateOverlay } = useAppOverlaysContext()

  const { device } = useBreakpoint()
  const ActiveDeviceIcon = BUILDER_DEVICES[device].Icon

  // State lokal untuk mengelola data overlay yang sedang diedit
  const [localOverlay, setLocalOverlay] = useState<BuilderOverlay>(overlay)
  const [, setTempTitle] = useState(overlay.title)
  const [, setTempSizeValue] = useState<number>(getDrawerSize(overlay.size[device]).v1)
  const [, setTempHeightValue] = useState<number>(() => {
    const size = overlay.size[device]
    const [, height] = size.split(',')

    // If height is undefined or "undefined", return default value
    if (!height || height === 'undefined') {
      return 50
    }

    // Remove % and parse as integer, fallback to 50 if parsing fails
    const heightValue = parseInt(height.replace('%', ''))
    return isNaN(heightValue) ? 50 : heightValue
  })

  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // Update localOverlay ketika overlay prop berubah
  useEffect(() => {
    // Hanya update jika tidak ada perubahan lokal
    if (!hasLocalChanges) {
      setLocalOverlay(overlay)
    }
  }, [overlay, hasLocalChanges])

  // Update temp values ketika localOverlay berubah
  useEffect(() => {
    setTempTitle(localOverlay.title)
    setTempSizeValue(getDrawerSize(localOverlay.size[device]).v1)

    const size = localOverlay.size[device]
    const [, height] = size.split(',')
    if (!height || height === 'undefined') {
      setTempHeightValue(50)
    } else {
      const heightValue = parseInt(height.replace('%', ''))
      setTempHeightValue(isNaN(heightValue) ? 50 : heightValue)
    }
  }, [localOverlay, device])

  useEffect(() => {
    setTempTitle(overlay.title)
  }, [overlay.title])

  const [updatedValue, setUpdatedValue] = useState<{ key: string; value: any } | null>(null)
  const [isApplying, setIsApplying] = useState(false)

  const updateLocalAndAPI = useCallback(
    (updater: (overlay: BuilderOverlay) => void) => {
      setLocalOverlay((prev) => {
        const updated = { ...prev }
        updater(updated)
        return updated
      })
      updateOverlay(index, updater)
      setHasLocalChanges(true)
    },
    [updateOverlay, index]
  )

  const localOverlayRef = useRef(localOverlay)
  useEffect(() => {
    localOverlayRef.current = localOverlay
  }, [localOverlay])

  // Apply changes to the overlay
  const handleApplyChanges = async () => {
    if (!updatedValue) return

    setIsApplying(true)

    updateLocalAndAPI((prev) => {
      const key = updatedValue.key as keyof BuilderOverlay
      const prevValues = prev[key]

      const updatedPrevValues = Object.keys(prevValues).reduce(
        (acc, device) => {
          acc[device] = updatedValue.value
          return acc
        },
        {} as typeof prevValues
      )

      // @ts-expect-error
      prev[key] = { ...updatedPrevValues }
    })

    setIsApplying(false)

    setUpdatedValue(null)
    setHasLocalChanges(false) // Reset flag
  }

  const ButtonApplyToAll = () => {
    return (
      <>
        <hr className='mt-2 mb-4 bdr' />

        <div className='w-[calc(100% - 24px)] mb-4 flex justify-center'>
          <button
            className={clsx(
              'text-gray-100 text-xs py-2 px-3 rounded-sm w-full transition-colors',
              isApplying
                ? 'bg-primary-700 dark:bg-primary-700/85 cursor-wait'
                : 'bg-primary-500 dark:bg-primary-500/85 hover:bg-primary-600'
            )}
            onClick={handleApplyChanges}
            disabled={isApplying}
          >
            {isApplying ? t('Saving changes...') : t('Save changes across all screen sizes')}
          </button>
        </div>
      </>
    )
  }

  const getFilteredAccordionItems = useCallback(() => {
    if (!search.trim()) {
      return {
        showPlacement: true,
      }
    }

    const searchLower = search.toLowerCase()

    return {
      showPlacement: 'placement'.includes(searchLower) || 'slide-in direction'.includes(searchLower),
    }
  }, [search])

  const filteredItems = getFilteredAccordionItems()

  return (
    <Accordion openMultiple={false} initialActiveItems={['placement']}>
      {filteredItems.showPlacement && (
        <AccordionItem
          itemId='placement'
          title={<Trans>Slide-in direction</Trans>}
          righSection={<ActiveDeviceIcon className='w-3.5 h-3.5' />}
        >
          <Field>
            <div className='flex gap-2'>
              <SingleIconSelect
                options={overlayPlacements.map((p) => ({
                  id: p.value,
                  icon: p.icon,
                  title: getDirectionLabel(p.value),
                }))}
                selected={switchDirectionPlacement(localOverlay.placement[device]!)}
                onClick={(opt) => {
                  updateLocalAndAPI((prev) => {
                    const newPlacement = switchDirectionPlacement(opt.id)
                    prev.placement = {
                      ...prev.placement,
                      [device]: newPlacement,
                    }
                    setUpdatedValue({ key: 'placement', value: newPlacement })
                  })
                }}
              />
            </div>
          </Field>

          {updatedValue?.key === 'placement' && <ButtonApplyToAll />}
        </AccordionItem>
      )}
    </Accordion>
  )
}
